import React, { useState } from 'react'
import { useRouter } from '../../../hooks'
import { SearchableList } from '../SearchableList'
import {
  NoteCard,
  NoteEditorForm,
  NotesEmptyState,
  NotesNoResults,
  AddNoteHeaderButton,
  getTextFromHtml,
} from '../Notes'
import type { EntityNotesContentProps } from './EntityNotesContent.types'

const defaultTranslations = {
  title: 'Notes',
  loading: 'Loading notes...',
  searchPlaceholder: 'Search notes...',
  clearSearch: 'Clear search',
  addNote: 'Add Note',
  newNote: 'New Note',
  hint: 'Use the toolbar to format your note or record with AI',
  saveNote: 'Save Note',
  cancel: 'Cancel',
  emptyTitle: 'No notes yet',
  emptyDescription: 'Start adding notes to keep track of important information',
  noResultsTitle: 'No results found',
  noResultsDescription: 'Try adjusting your search terms',
  voiceNote: {
    recordWithAI: 'Record with AI',
    aiInfo: 'AI will transcribe and format your voice note automatically',
    stopRecording: 'Stop Recording',
    recording: 'Recording in progress...',
    processing: 'Processing your recording...',
    analyzing: 'Analyzing your voice note...',
    error: 'Sorry, something went wrong. Please try again.',
    cancel: 'Cancel',
    tryAgain: 'Try Again',
  },
}

export const EntityNotesContent: React.FC<EntityNotesContentProps> = ({
  notes,
  loading,
  searchQuery,
  setSearchQuery,
  isAddingNote,
  setIsAddingNote,
  editingNoteId,
  setEditingNoteId,
  deleteConfirmId,
  setDeleteConfirmId,
  voiceNotesService,
  createNote,
  updateNote,
  deleteNote,
  onClose,
  readOnly = false,
  translations,
}) => {
  const t = {
    ...defaultTranslations,
    ...translations,
    voiceNote: { ...defaultTranslations.voiceNote, ...translations?.voiceNote },
  }
  const [noteContent, setNoteContent] = useState('')
  const [isAnalyzingVoice, setIsAnalyzingVoice] = useState(false)
  const { query } = useRouter()
  const isBuilding = query.mode === 'building'

  const handleAddNote = async () => {
    if (readOnly || !createNote || !noteContent.trim()) return

    try {
      await createNote(noteContent)
      setNoteContent('')
      setIsAddingNote(false)
    } catch (error) {
      console.error('Error creating note:', error)
    }
  }

  const handleUpdateNote = async (noteId: number) => {
    if (readOnly || !updateNote || !noteContent.trim()) return

    try {
      await updateNote(noteId, noteContent)
      setNoteContent('')
      setEditingNoteId(null)
    } catch (error) {
      console.error('Error updating note:', error)
    }
  }

  const handleDeleteNote = async (noteId: number) => {
    if (readOnly || !deleteNote) return
    try {
      await deleteNote(noteId)
      setDeleteConfirmId(null)
    } catch (error) {
      console.error('Error deleting note:', error)
    }
  }

  const startEdit = (note: any) => {
    setEditingNoteId(note.id)
    setNoteContent(note.content)
  }

  const cancelEdit = () => {
    setEditingNoteId(null)
    setNoteContent('')
  }

  const handleOnComplete = async (content: string | Blob) => {
    try {
      if (content instanceof Blob) {
        setIsAnalyzingVoice(true)
        if (voiceNotesService) {
          const response = isBuilding
            ? await voiceNotesService.processVoiceBuildingNote?.(content)
            : await voiceNotesService.processVoiceLandNote?.(content)

          if (response) {
            setNoteContent(response.formatted_html)
            setIsAnalyzingVoice(false)
          } else {
            console.warn('Voice processing not available for this entity type')
            setIsAnalyzingVoice(false)
          }
        } else {
          console.warn('Voice notes service not provided')
          setIsAnalyzingVoice(false)
        }
      } else {
        setNoteContent(content)
        setIsAnalyzingVoice(false)
      }
    } catch (error) {
      console.error('Error processing voice note:', error)
      setIsAnalyzingVoice(false)
    }
  }

  const filteredNotes = notes.filter((note) => {
    if (!searchQuery.trim()) return true
    const searchText =
      `${note.userName || note.authorName} ${getTextFromHtml(note.content)}`.toLowerCase()
    return searchText.includes(searchQuery.toLowerCase())
  })

  if (loading) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: '#666' }}>{t.loading}</p>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SearchableList
        icon="edit"
        title={t.title}
        count={notes.length}
        searchPlaceholder={t.searchPlaceholder}
        onSearch={setSearchQuery}
        onClose={onClose}
        searchable
        closable={!!onClose}
        initialQuery={searchQuery}
        searchAction={
          readOnly ? undefined : (
            <AddNoteHeaderButton
              label={t.addNote}
              onClick={() => setIsAddingNote(true)}
              disabled={isAddingNote}
            />
          )
        }
        emptyStateComponent={
          <NotesEmptyState
            title={t.emptyTitle}
            description={t.emptyDescription}
          />
        }
        noResultsComponent={
          <NotesNoResults
            title={t.noResultsTitle}
            description={t.noResultsDescription}
            action={{
              label: t.clearSearch,
              onClick: () => setSearchQuery(''),
            }}
          />
        }
        style={{ flex: 1 }}
      >
        {isAddingNote && (
          <NoteEditorForm
            title={t.newNote}
            hint={t.hint}
            content={noteContent}
            onChange={setNoteContent}
            onSave={handleAddNote}
            onCancel={() => {
              setIsAddingNote(false)
              setNoteContent('')
              setIsAnalyzingVoice(false)
            }}
            saveLabel={t.saveNote}
            aiRecordingProps={{
              onComplete: handleOnComplete,
              onError: (error) => {
                console.error('Voice note error:', error)
                setIsAnalyzingVoice(false)
              },
              onReset: () => {
                // Called when AI Assistant resets to idle
              },
              isAnalyzing: isAnalyzingVoice,
              voiceNotesService,
              translations: t.voiceNote,
            }}
          />
        )}

        {filteredNotes.map((note) => (
          <div
            key={note.id}
            data-searchable
            data-search-text={`${note.userName || note.authorName} ${getTextFromHtml(note.content)}`}
          >
            <NoteCard
              note={note}
              searchQuery={searchQuery}
              isEditing={!readOnly && editingNoteId === note.id}
              editContent={editingNoteId === note.id ? noteContent : ''}
              onEditContentChange={setNoteContent}
              onEdit={readOnly ? undefined : () => startEdit(note)}
              onSave={readOnly ? undefined : () => handleUpdateNote(note.id)}
              onCancelEdit={cancelEdit}
              onDelete={
                readOnly ? undefined : () => setDeleteConfirmId(note.id)
              }
              isDeleting={!readOnly && deleteConfirmId === note.id}
              onConfirmDelete={() => handleDeleteNote(note.id)}
              onCancelDelete={() => setDeleteConfirmId(null)}
            />
          </div>
        ))}
      </SearchableList>
    </div>
  )
}
